import { FC, ReactElement, useState, useEffect, useRef } from 'react'
import styled from 'styled-components'
import ListItem from 'components/ListItem'
import { device } from '../breakpoints'
import Filters from 'components/Filters'
import TextSearch from 'components/TextSearch'
import {
  ContentWidth,
  TextWidthExtended,
} from '@ta-interaktiv/react-disco-grid'
import { BiSlider } from 'react-icons/bi'
import ScrollUpButton from 'components/ScrollUpButton'
import { MdClose } from 'react-icons/md'

interface ListProps {}

const List: FC<ListProps> = (): ReactElement => {
  const [data, setData] = useState<Person[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [filters, setFilters] = useState(new Map())
  const [textFilteredData, setTextFilteredData] = useState<Person[]>([])
  const [filteredData, setFilteredData] = useState<Person[]>([])
  const [error, setError] = useState(null)
  const [showFilters, setShowFilters] = useState(false)
  const [isHidden, setIsHidden] = useState(false)
  const searchContainerRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    fetch(
      'https://storage.googleapis.com/wch-interactive/bilan-300-plus-riches/people-2025.json'
    )
      .then((response) => {
        if (!response.ok) {
          throw new Error('Network response was not ok')
        }
        return response.json()
      })
      .then((data) => setData(data))
      .catch((err) => setError(err.toString()))
  }, [])

  // console.log('data', data)
  // console.log('filteredData', filteredData)

  useEffect(() => {
    // console.log('filters', filters)

    const filteredData = textFilteredData.filter((item: any) => {
      let isMatching = true

      filters.forEach((value: string, key: string) => {
        if (key === 'mainSector') {
          if (item['mainSector'] !== value) {
            isMatching = false
          }
        } else if (key === 'residence') {
          if (!item[key].includes(value)) {
            isMatching = false
          }
        } else if (key === 'fortune') {
          const values = value.split(',')
          if (item['fortune'] < values[0] || item['fortune'] >= values[1]) {
            isMatching = false
          }
        } else if (key === 'fortuneEvolution') {
          if (item['fortuneEvolution'] !== value) {
            isMatching = false
          }
        }
      })
      return isMatching
    })

    setFilteredData(filteredData)
  }, [filters, textFilteredData, data])

  useEffect(() => {
    if (isHidden) {
      searchContainerRef.current?.scrollIntoView({
        behavior: 'smooth',
      })
    }
  }, [filteredData])

  if (error) {
    return <div>{error}</div>
  }

  return (
    <Container ref={searchContainerRef}>
      <TextWidthExtended>
        <>
          <Divider />
          <SearchContainer isHidden={isHidden}>
            <SearchContent>
              <TextSearchContainer>
                <TextSearch
                  data={data}
                  onChange={(data: Person[]) => {
                    setTextFilteredData(data)
                  }}
                />
                <FiltersButton
                  onClick={() => {
                    setShowFilters(!showFilters)
                  }}
                >
                  {!showFilters ? <BiSlider /> : <MdClose />}{' '}
                  <FilterText>Filtres</FilterText>
                </FiltersButton>
              </TextSearchContainer>
              <>
                <Filters
                  show={showFilters}
                  data={filteredData}
                  onFilterChange={(filters: Map<string, string>) => {
                    setFilters(filters)
                  }}
                />
              </>
              <InfosContainer>
                <ResultsCountContainer>
                  {filteredData.length > 0 ? (
                    <>
                      <b>{filteredData.length}</b> résultat
                      {filteredData.length > 1 && 's'}
                    </>
                  ) : (
                    <>Aucun résultat</>
                  )}
                </ResultsCountContainer>
              </InfosContainer>
            </SearchContent>
          </SearchContainer>
          <ScrollUpButton
            onHidden={(isHidden) => {
              setIsHidden(isHidden)
            }}
          />
          {filteredData.length > 0 && (
            <>
              <ItemsContainer>
                {filteredData.map((item: any, index: number) => (
                  <ListItem data={item} />
                ))}
              </ItemsContainer>
            </>
          )}
        </>
      </TextWidthExtended>
    </Container>
  )
}

const SearchContent = styled.div`
  width: 100%;
  margin: 0 auto;
  box-sizing: border-box;

  @media ${device.mobileS}, @media ${device.mobileM}, @media ${device.mobileL} {
    max-width: none;
  }

  @media ${device.tablet}, @media ${device.laptop} {
    max-width: 65vw;
  }
`

const InfosContainer = styled.div`
  display: flex;
  padding-bottom: 10px;
  justify-content: flex-start;
  font-family: var(--font-sans-serif);
  font-size: 0.65em;
  -webkit-font-smoothing: antialiased;

  @media ${device.mobileS}, @media ${device.mobileM}, @media ${device.mobileL} {
    padding: 0 15px;
  }

  @media ${device.tablet}, @media ${device.laptop} {
    padding: 0;
  }
`

const ResultsCountContainer = styled.div``

const Divider = styled.div`
  border-top: 2px solid ${({ theme }) => theme.grayColor};
  padding-top: 20px;
`

const Container = styled.div`
  font-size: 1.8rem;
  line-height: 3rem;
  margin-bottom: 80px;
`
const TextSearchContainer = styled.div`
  display: flex;

  @media ${device.mobileS}, @media ${device.mobileM}, @media ${device.mobileL} {
    padding: 15px;
    padding-bottom: 0px;
  }

  @media ${device.tablet}, @media ${device.laptop} {
    padding: 30px 0;
    padding-bottom: 0px;
  }
`

const SearchContainer = styled.div<{ isHidden: boolean }>`
  z-index: 1;
  width: 100%;
  background-color: ${({ theme }) => theme.siteBackground};
  color: ${({ theme }) => theme.textColor};
  left: 0;

  @media ${device.mobileS}, @media ${device.mobileM}, @media ${device.mobileL} {
    top: auto;
    bottom: 0;
    position: ${({ isHidden }) => (isHidden ? 'fixed' : 'relative')};
  }

  @media ${device.tablet}, @media ${device.laptop} {
    top: 0;
    position: sticky;
  }
`

const FiltersButton = styled.div`
  background-color: ${({ theme }) => theme.colorMain};
  color: white;
  font-family: var(--font-sans-serif);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0 20px;
  white-space: nowrap;
  cursor: pointer;
  line-height: 1;
  margin-left: 15px;
  transition: opacity 0.2s ease-in-out;

  &:hover {
    opacity: 0.8;
  }
`

const FilterText = styled.div`
  color: white;

  @media ${device.mobileS}, @media ${device.mobileM}, @media ${device.mobileL} {
    display: none;
    margin-left: 0;
  }

  @media ${device.tablet}, @media ${device.laptop} {
    display: block;
    margin-left: 10px;
  }
`

const ItemsContainer = styled.div`
  margin-top: 60px;
`

export default List
